import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import IconClose from "@/components/icons/icon-close";
import type { Question } from "./types";
import { FORMAT_OPTIONS } from "./types";

interface FlowItemQuestionProps {
	question: Question;
	isSelected: boolean;
	onSelect: () => void;
	onDelete: () => void;
	onFormatChange?: (format: Question["format"]) => void;
	canDelete: boolean;
	disabled?: boolean;
}

export default function FlowItemQuestion({ question, isSelected, onSelect, onDelete, onFormatChange, canDelete, disabled = false }: FlowItemQuestionProps) {
	const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: question.id });

	const style = {
		transform: CSS.Transform.toString(transform),
		transition,
	};

	const handleDeleteClick = (e: React.MouseEvent) => {
		e.stopPropagation();
		if (canDelete) {
			onDelete();
		}
	};

	const handleFormatChange = (e: React.MouseEvent, format: Question["format"]) => {
		e.stopPropagation();
		onFormatChange?.(format);
	};

	const formatLabel = FORMAT_OPTIONS.find((f) => f.value === question.format)?.label || "Text";

	return (
		<div className="relative">
			<div ref={setNodeRef} style={style} onClick={disabled ? undefined : onSelect} className={`flex items-center gap-3 relative ${isDragging ? "opacity-50" : ""}`}>
				{/* Step indicator circle */}
				<div className={`w-3 h-3 rounded-full shrink-0 z-10 ${isSelected ? "bg-primary ring-4 ring-primary/20" : "bg-gray-400"}`} />

				{/* Draggable content area */}
				<div
					className={`flex-1 flex items-center gap-2 py-2 px-3 rounded-lg transition-colors ${
						isSelected ? "bg-primary/5" : "hover:bg-gray-50"
					} ${disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"}`}
				>
					{/* Drag handle */}
					<button
						type="button"
						className="shrink-0 p-1 cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600"
						onClick={(e) => e.stopPropagation()}
						{...attributes}
						{...listeners}
					>
						<svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
							<circle cx="9" cy="6" r="1.5" />
							<circle cx="15" cy="6" r="1.5" />
							<circle cx="9" cy="12" r="1.5" />
							<circle cx="15" cy="12" r="1.5" />
							<circle cx="9" cy="18" r="1.5" />
							<circle cx="15" cy="18" r="1.5" />
						</svg>
					</button>

					{/* Question content */}
					<div className="flex-1 min-w-0 w-[180px]">
						{question.title ? (
							<span className={`text-sm font-medium truncate block ${isSelected ? "text-primary" : "text-primary"}`}>{question.title}</span>
						) : (
							<span className="text-sm font-medium truncate block text-tertiary italic">Untitled</span>
						)}
					</div>

					{/* Format chip */}
					<span className="shrink-0 text-xs px-1.5 py-0.5 rounded bg-gray-100 text-tertiary">{formatLabel}</span>

					{/* Delete button */}
					{canDelete && (
						<button
							type="button"
							onClick={handleDeleteClick}
							disabled={disabled}
							className="shrink-0 p-1.5 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-gray-400 hover:text-red-500 hover:bg-red-50"
							aria-label="Remove question"
						>
							<IconClose className="w-4 h-4" />
						</button>
					)}
				</div>
			</div>

			{/* Format panel shown below when selected */}
			{isSelected && (
				<div className="ml-6 mt-1 pl-6 py-2 rounded-lg bg-gray-50">
					<div className="flex items-center gap-1">
						<span className="text-xs mr-2 text-tertiary">Format:</span>
						{FORMAT_OPTIONS.map((option) => (
							<button
								key={option.value}
								type="button"
								onClick={(e) => handleFormatChange(e, option.value as Question["format"])}
								className={`px-2.5 py-1 text-xs rounded-md transition-colors ${
									question.format === option.value ? "bg-primary text-white" : "bg-white text-tertiary hover:bg-gray-100 border border-gray-200"
								}`}
							>
								{option.label}
							</button>
						))}
					</div>
				</div>
			)}
		</div>
	);
}
